import { ACCESS_PROTOCOL, exact, isUuid } from "./embed-protocol";

/** アクセストークンの読み取りに許す待ち時間（ms）。超えたらトークン無しで応答する。 */
const READ_TIMEOUT_MS = 5000;

/**
 * 子（Connect Feedback）へ短命アクセストークンを渡すための専用チャネル。
 *
 * 子が `connect` と一緒に送ってきた `MessagePort` 1 本だけを保持し、その port の
 * `read` 要求に対して `result` を返す。トークンは通常の postMessage 経路へは載せない。
 * 文書（documentNonce）が変わったら port を閉じ、古い文書の読み取り結果は捨てる。
 */
export function createFeedbackAccessChannel(options: {
  readonly origin: string;
  readonly instanceId: string;
  readonly frame: () => Window | null | undefined;
  readonly acceptsDocument: (nonce: string) => boolean;
  readonly read: () => Promise<string | undefined>;
}) {
  let disposed = false;
  let port: MessagePort | undefined;
  let cancelRead: (() => void) | undefined;

  const close = (): void => {
    port?.close();
    port = undefined;
    cancelRead?.();
    cancelRead = undefined;
  };

  const receive = (event: MessageEvent<unknown>): void => {
    const value = event.data;
    if (
      disposed ||
      event.origin !== options.origin ||
      event.source !== options.frame() ||
      !exact(value, ["protocol", "version", "type", "instanceId", "documentNonce"]) ||
      value.protocol !== ACCESS_PROTOCOL ||
      value.version !== 1 ||
      value.type !== "connect" ||
      value.instanceId !== options.instanceId ||
      !isUuid(value.documentNonce) ||
      !options.acceptsDocument(value.documentNonce) ||
      event.ports.length !== 1
    ) {
      return;
    }
    close();
    const active = event.ports[0];
    if (!active) return;
    port = active;
    const nonce = value.documentNonce;
    let pending = false;
    active.onmessage = (portEvent: MessageEvent<unknown>): void => {
      const request = portEvent.data;
      if (
        disposed ||
        port !== active ||
        !options.acceptsDocument(nonce) ||
        pending ||
        !exact(request, ["type", "requestId"]) ||
        request.type !== "read" ||
        !isUuid(request.requestId)
      ) {
        return;
      }
      pending = true;
      const requestId = request.requestId;
      let complete = false;
      const timer = setTimeout(() => finish(undefined), READ_TIMEOUT_MS);
      const finish = (accessToken: string | undefined): void => {
        if (complete) return;
        complete = true;
        pending = false;
        clearTimeout(timer);
        if (cancelRead === cancel) cancelRead = undefined;
        if (!disposed && port === active && options.acceptsDocument(nonce)) {
          try {
            active.postMessage({
              type: "result",
              requestId,
              ...(accessToken === undefined ? {} : { accessToken }),
            });
          } catch {
            // port が既に閉じられている場合は黙って後片付けする。
            close();
          }
        }
      };
      const cancel = (): void => {
        complete = true;
        clearTimeout(timer);
      };
      cancelRead = cancel;
      void Promise.resolve()
        .then(options.read)
        .then(finish, () => finish(undefined));
    };
    active.start();
  };

  window.addEventListener("message", receive);

  return {
    reset: close,
    dispose(): void {
      disposed = true;
      close();
      window.removeEventListener("message", receive);
    },
  };
}
